/**
 * ActionsMenu — menu déroulant d'actions générique (bouton + role="menu")
 *
 * Modèle d'accessibilité repris de VetCaseActionsMenu (aria-haspopup,
 * aria-expanded, fermeture au clic extérieur), complété du clavier attendu
 * pour un menu ARIA : Entrée / Espace / Flèche bas ouvrent sur le premier
 * élément, flèches haut/bas et Début/Fin naviguent, Échap referme et rend le
 * focus au bouton, Tab referme. Éléments de 44 px de haut (cible tactile).
 *
 * Props :
 *   - label     : texte visible du bouton (et nom accessible du menu)
 *   - Icon      : icône lucide du bouton (optionnelle)
 *   - items     : [{ key, label, Icon, onSelect, danger }]
 *   - darkMode  : booléen
 *   - buttonStyle : style du bouton déclencheur (fusionné)
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { getTheme, TYPE, RADIUS } from '../constants/theme';
import { semanticColors } from '../constants/semanticColors';

export default function ActionsMenu({ label, Icon, items, darkMode = false, buttonStyle }) {
  const t = getTheme(darkMode);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const itemRefs = useRef([]);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    function onDocMouseDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Focus du premier élément à l'ouverture (clavier comme souris)
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  const close = ({ restoreFocus = true } = {}) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const focusItem = (index) => {
    const count = items.length;
    itemRefs.current[(index + count) % count]?.focus();
  };

  const onButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onMenuKeyDown = (e) => {
    const current = itemRefs.current.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusItem(current + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusItem(current - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusItem(0); }
    else if (e.key === 'End') { e.preventDefault(); focusItem(items.length - 1); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') { close({ restoreFocus: false }); }
  };

  const select = (item) => {
    close({ restoreFocus: false });
    item.onSelect();
  };

  const dangerColor = darkMode ? t.danger : semanticColors.redText;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onButtonKeyDown}
        style={buttonStyle}
      >
        {Icon && <Icon size={15} aria-hidden="true" />} {label}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: '100%',
            marginTop: 6,
            padding: 4,
            background: t.surfaceElevated,
            border: `1px solid ${t.border}`,
            borderRadius: RADIUS.md,
            boxShadow: t.shadowElevated,
            zIndex: 2000,
          }}
        >
          {items.map((item, i) => (
            <button
              key={item.key}
              ref={(el) => { itemRefs.current[i] = el; }}
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => select(item)}
              style={{
                ...TYPE.body,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                minHeight: 44,
                padding: '0 12px',
                border: 'none',
                borderRadius: RADIUS.sm,
                background: 'transparent',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                cursor: 'pointer',
                color: item.danger ? dangerColor : t.textPrimary,
              }}
            >
              {item.Icon && <item.Icon size={16} aria-hidden="true" />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
